import ProtectedRouter from "@/context/auth/ProtectedRouter";
import { TenantProvider } from "@/context/TenantContext";
import { useTenant } from "@/context/useTenant";
import StarterLayout from "@/layouts/StarterLayout";
import NotFoundPage from "@/pages/NotFoundPage";
import routes, { navigationRoutes } from "@/utils/routes";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import Pts from "./project/PTS/app.tsx";
import RedirectPtsToRoot from "./project/PTS/components/RedirectPtsToRoot";
import PTSAdminLogin from "./project/PTS/pages/Auth/AdminLogin";
import PTSForgotPassword from "./project/PTS/pages/Auth/ForgotPassword";
import PTSNavigationRoutes from "./project/PTS/utils/navigationRoutes";
import PtsSsoHandoff from "./project/PTS/pages/PtsSsoHandoff";

function App() {
  return (
    <TenantProvider>
      <AppWithRouter />
    </TenantProvider>
  );
}

const AppWithRouter = () => {
  const { tenantId } = useTenant();

  // Tenant ID comes from VITE_X_TENANTID in .env (or localStorage)
  if (!tenantId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Tenant ID Required</h1>
          <p className="text-gray-600">
            Set VITE_X_TENANTID in your .env file.
          </p>
        </div>
      </div>
    );
  }
  const publicRoutes = routes.find(m => m.type === "public")?.routes ?? [];
  // Exclude forgot-password-reset so PTS ForgotPassword page is used for /forgot-password-reset/:tokenId
  const publicRoutesWithoutForgotReset = publicRoutes.filter(
    r => r.path !== navigationRoutes.FORGOT_PASSWORD_RESET_ROUTE
  );
  // const authRoutes = routes.find(m => m.type === "auth")?.routes ?? [];
  return (
    <BrowserRouter>
      <Routes>
        {/* Main app public routes at top level so /login always opens (never caught by PTS * route) */}
        {publicRoutesWithoutForgotReset.map(({ path, element: Page }) => (
          <Route key={path} path={path} element={<StarterLayout />}>
            <Route index element={<Page />} />
          </Route>
        ))}

        <Route element={<TopLevelComponent />}>
          {/* Redirect root to home */}
          <Route
            path="/"
            element={<Navigate to="/employee-dashboard" replace />}
          />
          {/* Redirect /pts and /pts/* to /home and /* so PTS runs at root */}
          <Route
            path="pts"
            element={<Navigate to="/employee-dashboard" replace />}
          />
          <Route path="pts/*" element={<RedirectPtsToRoot />} />

          {/* PTS auth routes must be before the catch-all "*" so /forgot-password-reset/:tokenId and /pts-login are matched first */}
          <Route
            path={PTSNavigationRoutes.ADMIN_LOGIN_PAGE}
            element={<PTSAdminLogin />}
          />
          <Route
            path={PTSNavigationRoutes.FORGOT_PASSWORD_RESET_ROUTE}
            element={<PTSForgotPassword />}
          />
          {/* SSO handoff — must be before ProtectedRouter so unauthenticated users can land here */}
          <Route
            path={PTSNavigationRoutes.SSO_HANDOFF}
            element={<PtsSsoHandoff />}
          />

          {/* All authenticated routes (A1 + PTS) open inside PTS layout */}
          <Route element={<ProtectedRouter />}>
            <Route path="*" element={<Pts />} />
          </Route>

          {/* Protected routes */}
          {/* <Route element={<ProtectedRouter />}>
            <Route element={<MainLayout />}>
              {authRoutes.map(({ path, element: Page }) => (
                <Route key={path} path={path} element={<Page />} />
              ))}
            </Route>
           
            <Route path="*" element={<Pts />} />
          </Route> */}

          {/* 404 without sidebar/navbar - outside all layouts */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

const TopLevelComponent = () => {
  return <Outlet />;
};

export default App;
